import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
type User = {
  id: string;
  email?: string;
  [key: string]: any;
};

const INACTIVITY_TIMEOUT = 8 * 60 * 60 * 1000; // 8 horas (igual a sessao da API)
const AUTH_TIMEOUT = 3000; // 3 segundos de timeout de segurança

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [inactivityTimer, setInactivityTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
    }
    window.REACT_APP_NAVIGATE('/login');
  };

  const resetInactivityTimer = () => {
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
    }
    const timer = setTimeout(() => {
      logout();
    }, INACTIVITY_TIMEOUT);
    setInactivityTimer(timer);
  };

  useEffect(() => {
    let authTimeoutId: ReturnType<typeof setTimeout>;
    let isMounted = true;

    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!isMounted) return;
        if (session?.user) {
          setUser(session.user);
          resetInactivityTimer();
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error('Erro ao verificar sessão:', error);
        if (isMounted) setUser(null);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    // Timeout de segurança: se o Supabase não responder em 3s, libera o loading
    authTimeoutId = setTimeout(() => {
      if (isMounted && loading) {
        setLoading(false);
      }
    }, AUTH_TIMEOUT);

    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!isMounted) return;
      if (session?.user) {
        setUser(session.user);
        resetInactivityTimer();
      } else {
        setUser(null);
        if (inactivityTimer) {
          clearTimeout(inactivityTimer);
        }
      }
    });

    return () => {
      isMounted = false;
      clearTimeout(authTimeoutId);
      subscription.unsubscribe();
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Monitorar atividade do usuário
  useEffect(() => {
    if (!user) return;
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    const handleActivity = () => resetInactivityTimer();
    events.forEach(event => document.addEventListener(event, handleActivity));
    return () => {
      events.forEach(event => document.removeEventListener(event, handleActivity));
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return { user, loading, logout };
}